import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiBuildPageHeader,
  MiButton,
  MiContentDrawer,
  MiDetailFields,
  MiFileUpload,
  MiIcon,
  MiList,
  MiLoader,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DOCUMENT_TYPES,
  ENTITY_NAMES,
  FILE_MIME_TYPE_EXTENSIONS,
  ICONS_WEB,
  INPUT_TYPES,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { useEdit, useRouter } from "@miplatform/hooks";
import {
  externalReferenceId,
  getFileTypeFromMimeType,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { DocumentCard } from "@mibuildweb/components";
import { Box, useMediaQuery } from "@mui/material";
import { useHomeDocumentsDrawerViewModel } from "./useHomeDocumentsDrawerViewModel";

export function HomeDocumentsDrawer({
  open,
  documents,
  loading,
  selectedDocument,
  setSelectedDocument = () => null,
  refreshHomeDetails = () => null,
  onClose = () => null,
}) {
  const router = useRouter();
  const { id } = router.params;
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [file, setFile] = useState(undefined);
  const [selectedDeleteFile, setSelectedDeleteFile] = useState(null);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const edit = useEdit({
    documentCategory: "",
    orderTemplateName: "",
    tradeName: "",
    orderTemplateId: "",
    tradeId: "",
  });
  const { documentCategory, allowedFileFormats = [] } = edit.edits;
  const { classes, theme } = useStyles();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const {
    loading: isLoading,
    filteredDocuments,
    uploadDocument,
    removeDocument,
    getDocumentCategoryOptions,
    documentCategoryOptions,
    getOrderTemplates,
    orderTemplates,
    trades,
    getTrades,
    downloadFile,
    printFile,
  } = useHomeDocumentsDrawerViewModel({ search, documents });

  useEffect(() => {
    Promise.all([
      getDocumentCategoryOptions(),
      getOrderTemplates(),
      getTrades(),
    ]);
  }, []);

  const documentCategoryField = [
    {
      label: "Document Category",
      name: "documentCategory",
      required: true,
      placeholder: "Select Document Category",
      field: "name",
      width: "100%",
      fieldType: INPUT_TYPES.SELECT,
      options: documentCategoryOptions,
      handleChange: (e) => {
        const { allowedFileFormats } = documentCategoryOptions.find(
          (item) => item.value === e.target.value
        );
        edit.update({
          documentCategory: e.target.value,
          allowedFileFormats,
        });
      },
    },
    {
      label: ENTITY_NAMES.ORDER_TEMPLATE,
      name: "orderTemplateId",
      required: true,
      placeholder: "Select Order Type",
      field: "name",
      width: "100%",
      handleChange: (e) => {
        const orderTemplate = orderTemplates.find(
          (item) => item.value === e.target.value
        );
        edit.update({
          orderTemplateId: orderTemplate.value,
          orderTemplateName: orderTemplate.name || "None",
          tradeId: orderTemplate.tradeId,
          tradeName: orderTemplate.tradeName,
        });
      },
      fieldType: INPUT_TYPES.SELECT,
      options: orderTemplates,
      validationRule: z.string().min(1, "Order Type is required."),
    },
    {
      label: "Trade",
      name: "tradeId",
      field: "name",
      width: "100%",
      required: true,
      readOnly: edit.getValue("orderTemplateId") !== "None",
      fieldType: INPUT_TYPES.SELECT,
      handleChange: (e) => {
        const trade = trades.find((item) => item.value === e.target.value);
        edit.update({
          tradeId: trade.value,
          tradeName: trade.label,
        });
      },
      options: trades,
      validationRule: z.string().min(1, "Trade is required."),
    },
  ];
  edit.setValidation(documentCategoryField);

  const handleFileChange = (file) => {
    setFile(file?.[0] || undefined);
  };

  const handleDocumentSelect = (document) => {
    if (selectedDocument?.id === document?.id) {
      setSelectedDocument(null);
    } else {
      setSelectedDocument(document);
    }
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    edit.reset();
  };

  const onSubmitDocument = async () => {
    const { orderTemplateId, tradeId, orderTemplateName, tradeName } =
      edit.edits;
    const externalData = {
      orderTemplateId,
      tradeId,
      orderTemplateName,
      tradeName,
    };
    const encodedData = encodeURIComponent(JSON.stringify(externalData));
    await uploadDocument({
      file,
      documentType: DOCUMENT_TYPES.UNKNOWN,
      category: documentCategory,
      externalData: encodedData,
      externalReferenceId: externalReferenceId.getHomeRefId({ homeId: id }),
    });
    await refreshHomeDetails();
    setSearch("");
    closeAddModal();
  };

  const openDeleteConfirmModal = (document) => {
    setSelectedDeleteFile(document);
    setDeleteConfirmModalOpen(true);
  };

  const deleteDocument = async () => {
    if (selectedDocument?.id === selectedDeleteFile?.id) {
      setSelectedDocument(null);
    }
    await removeDocument({
      id: selectedDeleteFile?.id,
      modifiedOn: selectedDeleteFile?.modifiedOn,
    });
    await refreshHomeDetails();
    setSearch("");
    setDeleteConfirmModalOpen(false);
  };

  const handleClose = () => {
    onClose();
    setSearch("");
    setSelectedDocument(null);
  };

  const header = (
    <MiBuildPageHeader
      title="All Documents"
      titleVariant="h6"
      titleColor="primary.main"
      titleFontWeight={theme.fontWeight.semiBold}
      titleIcon={
        <Box
          backgroundColor={theme.palette.primary.light}
          borderRadius={2}
          padding={1}
          width={28}
          height={28}
        >
          <MiIcon
            path={mdi[ICONS_WEB.DOCUMENT]}
            size={0.8}
            color={theme.palette.primary.dark}
            data-testid="left-icon"
          />
        </Box>
      }
      hasDivider
      style={{ minHeight: 48 }}
      search={search}
      searchPlaceholder="Find Document"
      onHandleSearch={(e) => setSearch(e.target.value)}
      actionComponent={
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          className={classes.headerActions}
        >
          <MiButton
            title="Upload File"
            startIcon={{ path: mdi[ICONS_WEB.UPLOAD] }}
            sx={{ height: 34 }}
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={() => setAddModalOpen(true)}
            data-testid="add-document-btn"
          />
          <MiButton
            startIcon={{
              path: mdi[ICONS_WEB.CLOSE],
              iconColor: theme.palette.grey.main,
            }}
            onClick={handleClose}
            data-testid="right-icon-button"
            style={{ padding: theme.spacing(1) }}
          />
        </Box>
      }
    />
  );

  const DRAWER_TOP_OFFSET =
    (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
    MIBUILD_PAGE_HEADER_HEIGHT;

  return (
    <MiContentDrawer
      variant="persistent"
      open={open}
      onClose={handleClose}
      Header={header}
      slotProps={{
        transition: {
          unmountOnExit: true,
        },
      }}
      hideBackdrop
      sx={{
        "& > .MuiPaper-root": {
          top: DRAWER_TOP_OFFSET,
          height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
        },
      }}
    >
      <Box className={classes.docCardContainer}>
        {loading || isLoading ? (
          <MiLoader isLoading style={{ position: "absolute", top: "45%" }} />
        ) : (
          <MiList
            viewType="card"
            data={filteredDocuments || []}
            groupedMode
            showGroupCount
            CardComponent={DocumentCard}
            cardProps={{
              attributes: (doc) => [
                doc?.category,
                doc?.orderTemplateName,
                doc?.tradeName,
                getFileTypeFromMimeType(doc?.contentType),
              ],
              onPrint: printFile,
              onDelete: openDeleteConfirmModal,
              onDownload: downloadFile,
              onSelect: handleDocumentSelect,
              selectedDocumentId: selectedDocument?.id || "",
            }}
            loading={isLoading}
            hideListPagination
            groupedCriteria={(item) => item.orderTemplateName}
            groupedDataSort={(a, b) => {
              if (a[0] === "None") return 1;
              if (b[0] === "None") return -1;
              return a[0].localeCompare(b[0]);
            }}
            emptyListProps={{
              icon: mdi[ICONS_WEB.DOCUMENT],
              text: "No Documents found",
              helperText: !search
                ? "Click Upload File to add a new Document"
                : "Please adjust your search criteria and try again",
            }}
          />
        )}
      </Box>

      <MiModal
        title="Upload a Document"
        open={addModalOpen}
        closeModal={closeAddModal}
        topRightClose={closeAddModal}
        actions={[
          {
            name: "Cancel",
            size: "medium",
            onClick: closeAddModal,
          },
          {
            name: "Submit",
            variant: BUTTON_VARIANTS.CONTAINED,
            size: "medium",
            color: BUTTON_COLORS.PRIMARY,
            onClick: onSubmitDocument,
            disabled: !file || file.error || !edit.isValid(),
          },
        ]}
      >
        <MiDetailFields detailFields={documentCategoryField} edit={edit} />
        <MiFileUpload
          onChange={(file) => handleFileChange(file)}
          dragAndDrop
          validFileTypes={allowedFileFormats}
          acceptableFormatText={`Acceptable formats: ${allowedFileFormats
            .map((mimeType) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
            .join(", ")}`}
          multiple={false}
          disabled={!documentCategory}
        />
      </MiModal>

      <MiModalConfirm
        title="Confirm Remove"
        description={`Are you sure you want to remove file ${
          selectedDeleteFile?.metadata_Title || selectedDeleteFile?.filename
        } from this home?`}
        open={deleteConfirmModalOpen}
        handlePositive={deleteDocument}
        handleNegative={() => setDeleteConfirmModalOpen(false)}
        className={classes.modal}
      />
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()(() => ({
  docCardContainer: {
    flex: "1 1 50%",
    maxWidth: "800px",
    overflowY: "auto",
    position: "relative",
  },
}));
